import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Point } from '../data';

/**
 * Walls drawn along an outline: glazing, terrace planter walls, the roof
 * parapet.
 *
 * The plan draws spaces, not walls, so a wall is inferred: a stretch of a
 * space's edge that no neighbouring space or core also runs along faces the
 * outside (or a terrace) and is glazed. Where two outlines meet, both edges
 * are the same line to within the tracing's half metre, and that stretch is
 * a party wall, left open so the interior reads.
 */

export type Segment = [Point, Point];

/** Two traced outlines that meet are within this of each other. */
const TOLERANCE_M = 0.45;
/** A stretch shorter than this is a tracing seam, not a wall. */
const MIN_RUN_M = 0.6;

export function edgesOf(points: Point[]): Segment[] {
  return points.map((p, i) => [p, points[(i + 1) % points.length]!]);
}

/**
 * The parts of `edge` that no segment in `others` runs along, as distances
 * from its start. Collinear within TOLERANCE_M counts as running along.
 */
export function openRuns(edge: Segment, others: Segment[]): [number, number][] {
  const [[ax, ay], [bx, by]] = edge;
  const length = Math.hypot(bx - ax, by - ay);
  if (length < MIN_RUN_M) return [];
  const ux = (bx - ax) / length;
  const uy = (by - ay) / length;
  const along = ([x, y]: Point) => (x - ax) * ux + (y - ay) * uy;
  const off = ([x, y]: Point) => Math.abs((x - ax) * -uy + (y - ay) * ux);

  const covered: [number, number][] = [];
  for (const [c, d] of others) {
    if (off(c) > TOLERANCE_M || off(d) > TOLERANCE_M) continue;
    const t0 = Math.max(0, Math.min(along(c), along(d)));
    const t1 = Math.min(length, Math.max(along(c), along(d)));
    if (t1 - t0 > 0.01) covered.push([t0, t1]);
  }
  covered.sort((p, q) => p[0] - q[0]);

  const runs: [number, number][] = [];
  let cursor = 0;
  for (const [t0, t1] of covered) {
    if (t0 > cursor) runs.push([cursor, t0]);
    cursor = Math.max(cursor, t1);
  }
  if (cursor < length) runs.push([cursor, length]);
  return runs.filter(([t0, t1]) => t1 - t0 >= MIN_RUN_M);
}

export function inside([x, y]: Point, polygon: Point[]) {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

export interface Run {
  /** Start point and unit direction, in plan metres. */
  origin: Point;
  dir: Point;
  /** Unit normal pointing out of the polygon the run belongs to. */
  out: Point;
  t0: number;
  t1: number;
}

/** Every open run of every edge of `polygon`, against `others`. */
export function runsOf(polygon: Point[], others: Segment[]): Run[] {
  const runs: Run[] = [];
  for (const edge of edgesOf(polygon)) {
    const [[ax, ay], [bx, by]] = edge;
    const length = Math.hypot(bx - ax, by - ay);
    if (length < MIN_RUN_M) continue;
    const dir: Point = [(bx - ax) / length, (by - ay) / length];
    // Whichever side of the edge's midpoint is outside the polygon.
    let out: Point = [dir[1], -dir[0]];
    const probe: Point = [(ax + bx) / 2 + out[0] * 0.2, (ay + by) / 2 + out[1] * 0.2];
    if (inside(probe, polygon)) out = [-out[0], -out[1]];
    for (const [t0, t1] of openRuns(edge, others)) {
      runs.push({ origin: [ax, ay], dir, out, t0, t1 });
    }
  }
  return runs;
}

/**
 * A box lying along a run: `along` long, `height` tall, `depth` thick,
 * centred `offset` metres outside the edge and `start` metres along it.
 */
function box(
  run: Run,
  start: number,
  along: number,
  y0: number,
  height: number,
  depth: number,
  offset: number,
  cx: number,
  cz: number,
) {
  const geometry = new THREE.BoxGeometry(along, height, depth);
  // BoxGeometry is long in x; turn it to the run's direction (plan y → world z).
  geometry.rotateY(Math.atan2(-run.dir[1], run.dir[0]));
  const mid = start + along / 2;
  geometry.translate(
    run.origin[0] + run.dir[0] * mid + run.out[0] * offset - cx,
    y0 + height / 2,
    run.origin[1] + run.dir[1] * mid + run.out[1] * offset - cz,
  );
  return geometry;
}

export interface WallSpec {
  y0: number;
  height: number;
  cx: number;
  cz: number;
}

/**
 * A curtain wall: one pane per run, framed by mullions and a sill, under a
 * deep fascia. The fascia is مطل's: every storey of the built building is
 * edged by a thick charcoal band (photos matal-1…3), not a thin slab line.
 */
export function curtainWall(runs: Run[], spec: WallSpec, mullionEveryM = 1.5, fasciaM = 0.75) {
  const { y0, cx, cz } = spec;
  const height = spec.height - fasciaM;
  const glass: THREE.BufferGeometry[] = [];
  const frame: THREE.BufferGeometry[] = [];
  const fascia: THREE.BufferGeometry[] = [];
  const fins: THREE.BufferGeometry[] = [];
  const leds: THREE.BufferGeometry[] = [];
  for (const run of runs) {
    const length = run.t1 - run.t0;
    glass.push(box(run, run.t0, length, y0, height, 0.04, 0.06, cx, cz));
    frame.push(box(run, run.t0, length, y0, 0.12, 0.16, 0.06, cx, cz));
    fascia.push(box(run, run.t0 - 0.15, length + 0.3, y0 + height, fasciaM, 0.4, 0.2, cx, cz));
    // A line of LED under the fascia's front edge: what lights the façade at night.
    leds.push(box(run, run.t0 - 0.1, length + 0.2, y0 + height - 0.06, 0.06, 0.08, 0.36, cx, cz));
    const bays = Math.max(1, Math.round(length / mullionEveryM));
    for (let i = 0; i <= bays; i++) {
      const t = run.t0 + (length * i) / bays;
      const start = Math.min(Math.max(t - 0.04, run.t0), run.t1 - 0.08);
      frame.push(box(run, start, 0.08, y0, height, 0.18, 0.06, cx, cz));
    }
    // A deep fin closing each end of the glass, as on the built façade, where
    // every bay of glazing is bracketed by one (clad in timber, matal-1).
    if (length > 2) {
      fins.push(box(run, run.t0, 0.35, y0, spec.height, 0.7, 0.3, cx, cz));
      fins.push(box(run, run.t1 - 0.35, 0.35, y0, spec.height, 0.7, 0.3, cx, cz));
    }
  }
  return {
    glass: merge(glass),
    frame: merge(frame),
    fascia: merge(fascia),
    fins: merge(fins),
    leds: merge(leds),
  };
}

/** A solid upstand along the runs: a terrace planter wall, the roof parapet. */
export function upstand(runs: Run[], spec: WallSpec, thickness = 0.25) {
  const { y0, height, cx, cz } = spec;
  return merge(
    runs.map((run) =>
      box(run, run.t0, run.t1 - run.t0, y0, height, thickness, -thickness / 2, cx, cz),
    ),
  );
}

function merge(parts: THREE.BufferGeometry[]) {
  if (!parts.length) return null;
  return mergeGeometries(parts, false);
}
